/*
 * Purpose: Global connector fix modal state — open full remediation UI instead of bare toasts
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { create } from 'zustand';
import type { ConnectorFixIssue } from '@/lib/connectorIssuePolicy';
import {
  classifyExecutionError,
  findWorkflowConnectorIssues,
} from '@/lib/connectorIssuePolicy';
import type { Workflow } from '@/stores/workflowStore';

export interface ConnectorFixSession {
  issue: ConnectorFixIssue;
  issues: ConnectorFixIssue[];
  workflowId?: string;
  onRetry?: () => void | Promise<void>;
}

interface ConnectorFixStore {
  open: boolean;
  session: ConnectorFixSession | null;
  openConnectorFix: (session: ConnectorFixSession) => void;
  closeConnectorFix: () => void;
}

export const useConnectorFixStore = create<ConnectorFixStore>((set) => ({
  open: false,
  session: null,
  openConnectorFix: (session) => set({ open: true, session }),
  closeConnectorFix: () => set({ open: false, session: null }),
}));

export function openConnectorFix(session: ConnectorFixSession): void {
  useConnectorFixStore.getState().openConnectorFix(session);
}

/** Open modal from execution error when classifiable; returns true if handled. */
export function openConnectorFixFromError(
  error: unknown,
  options: {
    onRetry?: () => void | Promise<void>;
    nodeId?: string;
    workflow?: Workflow | null;
  }
): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return false;

  const message = error instanceof Error ? error.message : String(error);
  const issue = classifyExecutionError(message, options.nodeId);
  if (!issue) return false;

  const related = options.workflow ? findWorkflowConnectorIssues(options.workflow) : [issue];
  const active = related.find((i) => i.kind === issue.kind) ?? issue;

  openConnectorFix({
    issue: active,
    issues: related.length > 0 ? related : [issue],
    onRetry: options.onRetry,
    workflowId: options.workflow?.id,
  });
  return true;
}

export function openConnectorFixForWorkflow(
  workflow: Workflow,
  options?: { onRetry?: () => void | Promise<void>; preferKind?: ConnectorFixIssue['kind'] }
): boolean {
  const issues = findWorkflowConnectorIssues(workflow);
  if (issues.length === 0) return false;

  const issue =
    (options?.preferKind && issues.find((i) => i.kind === options.preferKind)) ?? issues[0];

  openConnectorFix({
    issue,
    issues,
    onRetry: options?.onRetry,
    workflowId: workflow.id,
  });
  return true;
}
